// /set-password 公开页(D3 单页决策)。
// URL: /set-password?token=...
// - 加载时 GET /api/auth/set-password?token=xxx 校验 token 状态
// - 校验通过显示密码表单
// - 提交 POST /api/auth/set-password {token, new_password}
// - 成功后跳 /login
// 不挂 AdminLayout(公开页),不复用现有 /login 样式(简单)

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "../../api/client";

type State =
  | { phase: "verifying" }
  | { phase: "invalid"; reason: string }
  | { phase: "ready"; expiresAt: string }
  | { phase: "submitting" }
  | { phase: "error"; message: string }
  | { phase: "done" };

export function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState<State>({ phase: "verifying" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    if (!token) {
      setState({ phase: "invalid", reason: "URL に token がありません。" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          `/api/auth/set-password?token=${encodeURIComponent(token)}`,
          { credentials: "include" },
        );
        const body = await resp.json();
        if (cancelled) return;
        if (!body.success) {
          setState({
            phase: "invalid",
            reason: body.error?.message || "token が無効です。",
          });
        } else {
          setState({
            phase: "ready",
            expiresAt: body.data?.expires_at || "",
          });
        }
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "invalid", reason: "通信エラーが発生しました。" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setState({ phase: "error", message: "パスワードが一致しません。" });
      return;
    }
    setState({ phase: "submitting" });
    try {
      const resp = await fetch("/api/auth/set-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, new_password: password }),
      });
      const body = await resp.json();
      if (!body.success) {
        throw new ApiError(
          body.error?.code || "RF_ADMIN_SET_PASSWORD_FAILED",
          resp.status,
          body.error?.message || "設定に失敗しました。",
        );
      }
      setState({ phase: "done" });
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "設定に失敗しました。";
      setState({ phase: "error", message: msg });
    }
  }

  if (state.phase === "verifying") {
    return (
      <div className="set-password-page">
        <div className="set-password-card">確認中...</div>
      </div>
    );
  }
  if (state.phase === "invalid") {
    return (
      <div className="set-password-page">
        <div className="set-password-card">
          <h1>リンクが無効です</h1>
          <p>{state.reason}</p>
          <p>
            <a href="/login">ログイン画面に戻る</a>
          </p>
        </div>
      </div>
    );
  }
  if (state.phase === "done") {
    return (
      <div className="set-password-page">
        <div className="set-password-card">
          <h1>設定完了</h1>
          <p>パスワードが設定されました。ログイン画面に移動します...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="set-password-page">
      <div className="set-password-card">
        <h1>パスワードを設定</h1>
        {state.phase === "ready" && state.expiresAt && (
          <p className="set-password-expires">
            有効期限: {new Date(state.expiresAt).toLocaleString("ja-JP")}
          </p>
        )}
        <form onSubmit={onSubmit}>
          <div className="set-password-field">
            <label htmlFor="pw-new">新しいパスワード</label>
            <input
              id="pw-new"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <div className="set-password-field">
            <label htmlFor="pw-confirm">パスワード(確認)</label>
            <input
              id="pw-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <p className="set-password-hint">
            12文字以上、大小英字・数字・記号を含む
          </p>
          {state.phase === "error" && (
            <div className="set-password-error">{state.message}</div>
          )}
          <button
            type="submit"
            className="set-password-submit"
            disabled={state.phase === "submitting"}
          >
            {state.phase === "submitting" ? "設定中..." : "設定する"}
          </button>
        </form>
      </div>
    </div>
  );
}